import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { fmt$ } from "../lib/model";
import { resolveTheme, getThemePref } from "../lib/theme";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, Filler);

const PALETTES = {
  dark: { grid: "rgba(255,255,255,0.09)", text: "#a0a0a0", strong: "#f5f5f5", lime: "#c6f24e", limeSoft: "rgba(198,242,78,0.18)", bar: "rgba(245,245,245,0.65)", blue: "#c6f24e", mid: "#7d7d7d", ring: "#111", shades: ["#6b8c16", "#96c72c", "#c6f24e", "#dcf78f", "#eefbc8"], tipBg: "#1a1a1a", tipText: "#f5f5f5" },
  light: { grid: "rgba(0,0,0,0.08)", text: "#33383e", strong: "#08090b", lime: "#2f62f0", limeSoft: "rgba(47,98,240,0.16)", bar: "rgba(8,9,11,0.55)", blue: "#1a4bd8", mid: "#5d636a", ring: "#fff", shades: ["#12307f", "#2f62f0", "#5d92f8", "#95b8fb", "#cadcfe"], tipBg: "#ffffff", tipText: "#08090b" },
};

function useChartTheme() {
  const [t, setT] = useState(() => resolveTheme(getThemePref()));
  useEffect(() => {
    const on = () => setT(document.documentElement.dataset.theme === "light" ? "light" : "dark");
    on();
    window.addEventListener("xyz-theme", on);
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    mq.addEventListener("change", on);
    return () => { window.removeEventListener("xyz-theme", on); mq.removeEventListener("change", on); };
  }, []);
  return PALETTES[t] || PALETTES.dark;
}

const MONO = { family: "ui-monospace, Menlo, monospace" };
const baseOpts = (P) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: P.text, font: { size: 11 }, boxWidth: 12 } },
    tooltip: {
      bodyFont: MONO, titleFont: { family: "system-ui" }, backgroundColor: P.tipBg, titleColor: P.tipText, bodyColor: P.tipText, borderColor: P.grid, borderWidth: 1,
      callbacks: { label: (c) => `${c.dataset.label}: ${fmt$(c.parsed.y ?? c.parsed)}` },
    },
  },
});
const axisOpts = (P) => ({
  x: { grid: { color: P.grid }, ticks: { color: P.text, font: { size: 10 } } },
  y: { grid: { color: P.grid }, ticks: { color: P.text, font: { size: 10 }, callback: (v) => fmt$(v) } },
});

export function RevenueSdeChart({ hy }) {
  const P = useChartTheme();
  const data = {
    labels: hy.map((h) => String(h.year)),
    datasets: [
      { type: "bar", label: "Revenue", data: hy.map((h) => h.revenue), backgroundColor: P.bar, order: 2 },
      { type: "line", label: "Adj. SDE", data: hy.map((h) => h.sde), borderColor: P.lime, backgroundColor: P.lime, tension: 0.3, order: 1, yAxisID: "y" },
    ],
  };
  return <div style={{ height: 260 }}><Bar data={data} options={{ ...baseOpts(P), scales: axisOpts(P) }} /></div>;
}

export function RevenueMixChart({ mix }) {
  const P = useChartTheme();
  const data = {
    labels: mix.map((m) => m.label),
    datasets: [{ data: mix.map((m) => m.value), backgroundColor: mix.map((_, i) => P.shades[i % P.shades.length]), borderColor: P.ring, borderWidth: 2 }],
  };
  const b = baseOpts(P);
  return (
    <div style={{ height: 260 }}>
      <Doughnut data={data} options={{ ...b, plugins: { ...b.plugins, legend: { position: "right", labels: { color: P.text, font: { size: 11 }, boxWidth: 12 } }, tooltip: { ...b.plugins.tooltip, callbacks: { label: (c) => `${c.label}: ${c.parsed}%` } } } }} />
    </div>
  );
}

export function CumulativeFcfChart({ rows }) {
  const P = useChartTheme();
  let cum = 0;
  const cumData = rows.map((r) => (cum += r.fcf));
  const data = {
    labels: rows.map((r) => `Yr ${r.y}`),
    datasets: [{ label: "Cumulative FCF ($)", data: cumData, borderColor: P.lime, backgroundColor: P.limeSoft, fill: true, tension: 0.3, pointRadius: 3 }],
  };
  return <div style={{ height: 260 }}><Line data={data} options={{ ...baseOpts(P), scales: axisOpts(P) }} /></div>;
}

export function ValueBridgeChart({ ex }) {
  const P = useChartTheme();
  const labels = ["Equity In", "Operating FCF", "Loan Paydown", "Exit Equity"];
  const vals = [-ex.equityInvested, ex.totalFCF, ex.loanPaydown, ex.equityAtExit];
  const colors = [P.blue, P.lime, P.mid, P.strong];
  const data = { labels, datasets: [{ label: "Value ($)", data: vals, backgroundColor: colors }] };
  const b = baseOpts(P);
  const opts = {
    ...b,
    indexAxis: "y",
    plugins: { ...b.plugins, legend: { display: false } },
    scales: {
      x: { grid: { color: P.grid }, ticks: { color: P.text, font: { size: 10 }, callback: (v) => fmt$(v) } },
      y: { grid: { color: P.grid }, ticks: { color: P.strong, font: { size: 11 } } },
    },
  };
  return <div style={{ height: 260 }}><Bar data={data} options={opts} /></div>;
}
